import { Link } from 'react-router-dom';

/**
 * RoomHora Logo — pure SVG/CSS, no PNG dependency.
 * Matches the brand: map-pin icon + "Room" (navy) + "Hora" (sky blue) + tagline.
 */

const SIZE_MAP = {
  small:   { icon: 26, fontSize: 18, tagSize: 8,  gap: 8  },
  default: { icon: 32, fontSize: 22, tagSize: 9,  gap: 10 },
  large:   { icon: 38, fontSize: 26, tagSize: 10, gap: 11 },
  xlarge:  { icon: 44, fontSize: 30, tagSize: 11, gap: 12 },
};

/* ─── Map-pin icon — matches brand style ──────────────────────────── */
const PinIcon = ({ size = 32 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 56 56"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <defs>
      <linearGradient id="pin-body" x1="10" y1="5" x2="46" y2="52" gradientUnits="userSpaceOnUse">
        <stop offset="0%"  stopColor="#38BDF8" />
        <stop offset="45%" stopColor="#0EA5E9" />
        <stop offset="100%" stopColor="#0369A1" />
      </linearGradient>
      <linearGradient id="pin-shine" x1="16" y1="8" x2="28" y2="24" gradientUnits="userSpaceOnUse">
        <stop offset="0%"   stopColor="rgba(255,255,255,0.5)" />
        <stop offset="100%" stopColor="rgba(255,255,255,0)" />
      </linearGradient>
      <filter id="pin-shadow" x="-20%" y="-10%" width="140%" height="130%">
        <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#0369A1" floodOpacity="0.35" />
      </filter>
    </defs>

    {/* Pin outer body */}
    <path
      d="M28 4C18.059 4 10 12.059 10 22C10 35.255 25.6 50.42 26.31 51.079C27.22 51.951 28.78 51.951 29.69 51.079C30.4 50.42 46 35.255 46 22C46 12.059 37.941 4 28 4Z"
      fill="url(#pin-body)"
      filter="url(#pin-shadow)"
    />
    {/* Shine overlay */}
    <path
      d="M28 4C18.059 4 10 12.059 10 22C10 27 12 31 16 34C14 30 13 26 13 22C13 13.716 19.716 7 28 7C32 7 35.6 8.6 38.2 11.2C35.4 7 32 4.8 28 4Z"
      fill="url(#pin-shine)"
    />

    {/* Inner circle — dark navy */}
    <circle cx="28" cy="22" r="13" fill="#0B1E3D" />
    {/* Inner circle highlight */}
    <circle cx="24" cy="17" r="3" fill="rgba(255,255,255,0.07)" />

    {/* House roof */}
    <path
      d="M21 24.5V20.5L28 15L35 20.5V24.5H32V21.5L28 18L24 21.5V24.5H21Z"
      fill="#38BDF8"
    />
    {/* House body */}
    <path
      d="M22 23.8H34V29H31V25.5H25V29H22V23.8Z"
      fill="white"
    />

    {/* Small sparkle dots — brand accent */}
    <circle cx="44" cy="10" r="1.5" fill="#38BDF8" opacity="0.8" />
    <circle cx="47" cy="16" r="1"   fill="#7DD3FC" opacity="0.6" />
    <circle cx="42" cy="6"  r="1"   fill="#BAE6FD" opacity="0.5" />
  </svg>
);

/* ─── Brand wordmark ──────────────────────────────────────────────── */
const Wordmark = ({ dark = false, size = 'default', showTagline = true }) => {
  const { fontSize, tagSize } = SIZE_MAP[size] || SIZE_MAP.default;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1, userSelect: 'none' }}>
      {/* RoomHora */}
      <span style={{
        fontFamily: "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif",
        fontWeight: 800,
        fontSize: `${fontSize}px`,
        letterSpacing: '-0.025em',
        lineHeight: 1,
      }}>
        <span style={{ color: dark ? '#FFFFFF' : '#0B1E3D' }}>Room</span>
        <span style={{ color: '#0EA5E9' }}>Hora</span>
      </span>
      {/* Tagline */}
      {showTagline && (
        <span style={{
          fontFamily: "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif",
          fontWeight: 600,
          fontSize: `${tagSize}px`,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: dark ? '#94A3B8' : '#64748B',
          marginTop: '3px',
          lineHeight: 1,
        }}>
          Map-First Rental Discovery
        </span>
      )}
    </div>
  );
};

/* ─── LogoFull — light backgrounds (navbar, login) ─────────────────── */
export const LogoFull = ({ className = '', showTagline = true, size = 'default' }) => {
  const { icon, gap } = SIZE_MAP[size] || SIZE_MAP.default;
  return (
    <Link
      to="/"
      className={`group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/50 rounded-xl py-1 ${className}`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: `${gap}px`, textDecoration: 'none' }}
      data-testid="logo-link"
      aria-label="RoomHora — Go to homepage"
    >
      <div style={{ transition: 'transform 0.2s ease' }}
           className="group-hover:scale-105">
        <PinIcon size={icon} />
      </div>
      <Wordmark size={size} showTagline={showTagline} />
    </Link>
  );
};

/* ─── LogoFullDark — dark backgrounds (hero, footer) ───────────────── */
export const LogoFullDark = ({ className = '', showTagline = true, size = 'default' }) => {
  const { icon, gap } = SIZE_MAP[size] || SIZE_MAP.default;
  return (
    <Link
      to="/"
      className={`group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50 rounded-xl py-1 ${className}`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: `${gap}px`, textDecoration: 'none' }}
      data-testid="logo-link"
      aria-label="RoomHora — Go to homepage"
    >
      <div style={{ transition: 'transform 0.2s ease', filter: 'drop-shadow(0 0 8px rgba(14,165,233,0.4))' }}
           className="group-hover:scale-105">
        <PinIcon size={icon} />
      </div>
      <Wordmark dark size={size} showTagline={showTagline} />
    </Link>
  );
};

/* ─── LogoIconOnly — compact / mobile ──────────────────────────────── */
export const LogoIconOnly = ({ size = 32, className = '' }) => (
  <Link
    to="/"
    className={`inline-flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/50 rounded-lg ${className}`}
    data-testid="logo-link"
    aria-label="RoomHora — Go to homepage"
  >
    <PinIcon size={size} />
  </Link>
);

export default LogoFull;
